import React, { useState, useEffect } from 'react';
import { Card, Group, Title, Text, Button, Stack, Divider, Table, ActionIcon, Badge, TextInput, Alert } from '@mantine/core';
import { Download, Search, FileText, FileArchive, FileSpreadsheet, FileImage, File as FileIcon, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

interface DownloadFile {
  id: string;
  filename: string;
  displayName: string;
  description: string;
  fileSize: string;
  fileType: 'pdf' | 'zip' | 'excel' | 'image' | 'document' | 'other';
  uploadDate: string;
  downloadCount: number;
  category: string;
}

const DownloadsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [files, setFiles] = useState<DownloadFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load files from API
  useEffect(() => {
    const loadFiles = async () => {
      try {
        const response = await api.get('/downloads/files');
        setFiles(response.data.files || []);
      } catch (err) {
        setError('Fehler beim Laden der Download-Dateien.');
        setTimeout(() => setError(null), 3000);
      } finally {
        setLoading(false);
      }
    };

    loadFiles();
  }, []);

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return <FileText size={16} color="red" />;
      case 'zip':
        return <FileArchive size={16} color="orange" />;
      case 'excel':
        return <FileSpreadsheet size={16} color="green" />;
      case 'image':
        return <FileImage size={16} color="blue" />;
      case 'document':
        return <FileText size={16} color="blue" />;
      default:
        return <FileIcon size={16} color="gray" />;
    }
  };

  const getFileTypeColor = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return 'red';
      case 'zip':
        return 'orange';
      case 'excel':
        return 'green';
      case 'image':
        return 'blue';
      case 'document':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const handleDownload = async (file: DownloadFile) => {
    try {
      // Use the actual API endpoint with axios
      const response = await api.get(`/downloads/download/${file.filename}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Update download count
      setFiles(prev => prev.map(f => 
        f.id === file.id ? { ...f, downloadCount: f.downloadCount + 1 } : f
      ));
    } catch (err) {
      setError('Download fehlgeschlagen. Bitte versuchen Sie es später erneut.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const filteredFiles = files.filter(file =>
    file.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categories = Array.from(new Set(files.map(f => f.category)));

  if (loading) {
    return (
      <Stack gap="lg">
        <Group gap="md" mb="xs" style={{ color: 'var(--tt-dark)' }}>
          <Title order={2} style={{ fontWeight: 300 }}>Downloads</Title>
        </Group>
        <Text c="dimmed">Lade Downloads...</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Group gap="md" mb="xs" style={{ color: 'var(--tt-dark)' }}>
        <Title order={2} style={{ fontWeight: 300 }}>Downloads</Title>
      </Group>
      <Text c="dimmed">Hier finden Sie Dokumente, Software und andere nützliche Dateien zum Herunterladen.</Text>

      {error && (
        <Alert icon={<AlertCircle size={16} />} color="red" title="Fehler">
          {error}
        </Alert>
      )}

      <Card withBorder>
        <Group justify="space-between" mb="md">
          <TextInput
            placeholder="Suchen..."
            leftSection={<Search size={16} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, maxWidth: '400px' }}
          />
        </Group>

        {categories.length > 0 && (
          <Group gap="xs" mb="md">
            <Text size="sm" fw={500}>Kategorien:</Text>
            {categories.map(category => (
              <Badge key={category} variant="light" size="sm">
                {category}
              </Badge>
            ))}
          </Group>
        )}

        <Divider />

        {filteredFiles.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            {searchTerm ? 'Keine Dateien gefunden, die Ihrer Suche entsprechen.' : 'Keine Download-Dateien verfügbar.'}
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Datei</Table.Th>
                <Table.Th>Beschreibung</Table.Th>
                <Table.Th>Größe</Table.Th>
                <Table.Th>Hochgeladen</Table.Th>
                <Table.Th>Aktion</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredFiles.map((file) => (
                <Table.Tr key={file.id}>
                  <Table.Td>
                    <Group gap="sm">
                      {getFileIcon(file.fileType)}
                      <div>
                        <Text size="sm" fw={500}>{file.displayName}</Text>
                        <Text size="xs" c="dimmed">{file.filename}</Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{file.description}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{file.fileSize}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{file.uploadDate}</Text>
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="filled"
                      color="blue"
                      onClick={() => handleDownload(file)}
                      title="Herunterladen"
                    >
                      <Download size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
};

export default DownloadsPage;
